<template>
    <div class="blog-blocks">
        <template v-for="(block, index) in normalizedBlocks" :key="block.id || `${block.type}-${index}`">
            <component :is="getHeadingTag(block)" v-if="isHeadingBlock(block)" :class="getHeadingClass(block)">
                <NotionRichText :fragments="getRichText(block)" />
            </component>

            <div v-else-if="block.type === 'paragraph'" class="blog-block">
                <p class="text-lg mb-0">
                    <NotionRichText :fragments="getRichText(block)" />
                </p>
                <NotionBlockRenderer v-if="hasChildren(block)" :blocks="block.children" class="blog-block__nested" />
            </div>

            <blockquote v-else-if="block.type === 'quote'" class="blog-block border-left-3 border-primary pl-3 my-3 text-700">
                <NotionRichText :fragments="getRichText(block)" />
                <NotionBlockRenderer v-if="hasChildren(block)" :blocks="block.children" class="blog-block__nested" />
            </blockquote>

            <ol
                v-else-if="block.type === 'ordered_list'"
                class="blog-block blog-block--list"
                :start="block.startIndex"
            >
                <li v-for="item in block.items" :key="item.id" class="blog-block__list-item">
                    <NotionRichText :fragments="getRichText(item)" />
                    <NotionBlockRenderer
                        v-if="hasChildren(item)"
                        :blocks="item.children"
                        class="blog-block__nested"
                    />
                </li>
            </ol>

            <ul v-else-if="block.type === 'bulleted_list'" class="blog-block blog-block--bulleted-list">
                <li v-for="item in block.items" :key="item.id" class="blog-block__list-item">
                    <NotionRichText :fragments="getRichText(item)" />
                    <NotionBlockRenderer
                        v-if="hasChildren(item)"
                        :blocks="item.children"
                        class="blog-block__nested"
                    />
                </li>
            </ul>

            <div v-else-if="block.type === 'callout'" class="blog-block blog-block--callout">
                <div class="blog-block__callout-icon">{{ getCalloutIcon(block) }}</div>
                <div class="blog-block__callout-content">
                    <NotionRichText :fragments="getRichText(block)" />
                    <NotionBlockRenderer v-if="hasChildren(block)" :blocks="block.children" class="blog-block__nested" />
                </div>
            </div>

            <details v-else-if="block.type === 'toggle'" class="blog-block my-3">
                <summary class="cursor-pointer text-lg font-medium">
                    <NotionRichText :fragments="getRichText(block)" />
                </summary>
                <NotionBlockRenderer v-if="hasChildren(block)" :blocks="block.children" class="blog-block__nested mt-3" />
            </details>

            <hr v-else-if="block.type === 'divider'" class="blog-block blog-block--divider" />

            <figure v-else-if="block.type === 'image'" class="blog-block blog-block--image">
                <a v-if="block.href" :href="block.href" target="_blank" rel="noopener noreferrer" class="blog-block__image-link" @click="handleLinkClick($event, block.href)">
                    <img :src="getMediaSrc(block)" :alt="getImageAlt(block)" class="blog-block__image" />
                </a>
                <img v-else :src="getMediaSrc(block)" :alt="getImageAlt(block)" class="blog-block__image" />
                <figcaption v-if="getCaptionFragments(block).length" class="blog-block__caption">
                    <NotionRichText :fragments="getCaptionFragments(block)" />
                </figcaption>
            </figure>

            <div v-else-if="block.type === 'file'" class="blog-block my-3">
                <a :href="block.url" target="_blank" rel="noopener noreferrer" class="text-primary" @click="handleLinkClick($event, block.url)">
                    {{ getFileLabel(block) }}
                </a>
            </div>

            <section
                v-else-if="block.type === 'column_list' && block.columns && block.columns.length"
                class="blog-block blog-block--columns"
                :style="getColumnsStyle(block.columns)"
            >
                <div v-for="column in block.columns" :key="column.id" class="blog-block__column">
                    <NotionBlockRenderer :blocks="column.children" />
                </div>
            </section>

            <div v-else-if="block.type === 'table' && block.rows && block.rows.length" class="blog-block blog-block--table-wrap">
                <table class="blog-block__table">
                    <thead v-if="block.hasColumnHeader">
                        <tr>
                            <th
                                v-for="(cell, cellIndex) in block.rows[0].cells"
                                :key="`head-${block.rows[0].id}-${cellIndex}`"
                                :scope="block.hasRowHeader && cellIndex === 0 ? 'row' : 'col'"
                                class="blog-block__table-head"
                            >
                                <NotionRichText :fragments="cell" />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in getBodyRows(block)" :key="row.id">
                            <component
                                :is="block.hasRowHeader && cellIndex === 0 ? 'th' : 'td'"
                                v-for="(cell, cellIndex) in row.cells"
                                :key="`${row.id}-${cellIndex}`"
                                :scope="block.hasRowHeader && cellIndex === 0 ? 'row' : null"
                                :class="block.hasRowHeader && cellIndex === 0 ? 'blog-block__table-head blog-block__table-head--row' : 'blog-block__table-cell'"
                            >
                                <NotionRichText :fragments="cell" />
                            </component>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-else-if="isExternalBlock(block)" class="blog-block my-3">
                <a :href="block.url" target="_blank" rel="noopener noreferrer" class="text-primary" @click="handleLinkClick($event, block.url)">
                    {{ block.url }}
                </a>
            </div>

            <pre v-else-if="block.type === 'code'" class="blog-block surface-100 border-round-xl p-3 overflow-x-auto"><code>{{ getCodeText(block) }}</code></pre>

            <div v-else-if="hasText(block)" class="blog-block">
                <p class="text-lg mb-0">
                    <NotionRichText :fragments="getRichText(block)" />
                </p>
                <NotionBlockRenderer v-if="hasChildren(block)" :blocks="block.children" class="blog-block__nested" />
            </div>
        </template>
    </div>
</template>

<script>
import NotionRichText from '@/components/blog/NotionRichText.vue'
import { trackArticleLinkConversion } from '@/assets/js/blog/tracking'

export default {
    name: 'NotionBlockRenderer',
    components: {
        NotionRichText,
    },
    props: {
        blocks: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        normalizedBlocks() {
            return this.groupListBlocks(this.blocks)
        },
    },
    methods: {
        handleLinkClick(clickEvent, url) {
            trackArticleLinkConversion(clickEvent, url)
        },
        isHeadingBlock(block) {
            return block.type === 'heading' || block.type === 'heading_2' || block.type === 'heading_3' || block.type === 'heading_4'
        },
        getHeadingTag(block) {
            if (block.type === 'heading_4') {
                return 'h4'
            }

            if (block.type === 'heading_3' || block.level === 3) {
                return 'h3'
            }

            return 'h2'
        },
        getHeadingClass(block) {
            if (block.type === 'heading_4') {
                return 'text-xl font-medium mb-2'
            }

            if (block.type === 'heading_3' || block.level === 3) {
                return 'text-xl font-medium mb-0'
            }

            return 'text-4xl font-medium mb-1'
        },
        buildTextFragment(text = '') {
            return {
                text,
                href: null,
                annotations: {},
            }
        },
        getRichText(block) {
            if (Array.isArray(block.richText) && block.richText.length) {
                return block.richText
            }

            if (typeof block.text === 'string' && block.text.trim()) {
                return [this.buildTextFragment(block.text)]
            }

            return []
        },
        getCaptionFragments(block) {
            if (Array.isArray(block.caption) && block.caption.length) {
                return block.caption
            }

            if (Array.isArray(block.captionRichText) && block.captionRichText.length) {
                return block.captionRichText
            }

            if (typeof block.caption === 'string' && block.caption.trim()) {
                return [this.buildTextFragment(block.caption)]
            }

            return []
        },
        hasChildren(block) {
            return Array.isArray(block.children) && block.children.length > 0
        },
        hasText(block) {
            return this.getRichText(block).length > 0
        },
        getImageAlt(block) {
            return this.getCaptionFragments(block).map((item) => item.text || '').join(' ').trim() || 'Article image'
        },
        getMediaSrc(block) {
            return block.src || block.url || ''
        },
        getFileLabel(block) {
            if (typeof block.caption === 'string' && block.caption.trim()) {
                return block.caption
            }

            const captionText = this.getCaptionFragments(block).map((item) => item.text || '').join(' ').trim()

            if (captionText) {
                return captionText
            }

            return block.url
        },
        getColumnsStyle(columns) {
            const ratios = columns.map((column) => `${column.widthRatio || 1}fr`).join(' ')

            return {
                gridTemplateColumns: ratios || `repeat(${columns.length}, minmax(0, 1fr))`,
            }
        },
        getBodyRows(block) {
            if (!block.hasColumnHeader) {
                return block.rows
            }

            return block.rows.slice(1)
        },
        getCalloutIcon(block) {
            if (typeof block.icon === 'string' && block.icon.trim()) {
                return block.icon
            }

            if (block.icon?.type === 'emoji' && block.icon.emoji) {
                return block.icon.emoji
            }

            return 'i'
        },
        isExternalBlock(block) {
            return block.type === 'embed' || block.type === 'bookmark' || block.type === 'video'
        },
        getCodeText(block) {
            if (typeof block.text === 'string' && block.text.length) {
                return block.text
            }

            return this.getRichText(block).map((item) => item.text || '').join('')
        },
        groupListBlocks(blocks = []) {
            const result = []
            let currentList = null

            blocks.forEach((block, index) => {
                const nextBlock = {
                    ...block,
                    id: block.id || `${block.type}-${index}`,
                    children: this.hasChildren(block) ? this.groupListBlocks(block.children) : [],
                }

                if (nextBlock.type === 'list_item') {
                    const listType = nextBlock.listType === 'numbered' ? 'ordered_list' : 'bulleted_list'

                    if (!currentList || currentList.type !== listType) {
                        currentList = {
                            id: `${nextBlock.id}-${listType}`,
                            type: listType,
                            startIndex: 1,
                            items: [],
                        }
                        result.push(currentList)
                    }

                    currentList.items.push(nextBlock)
                    return
                }

                currentList = null
                result.push(nextBlock)
            })

            return result
        },
    },
}
</script>